import React, { useEffect, useRef, useState } from 'react';
import './KPICard.css';

interface KeyMetricCardProps {
  color: string;
  title: string;
  value: string | number;
  measurement?: string;
  subtitle?: string;
  backgroundColor?: string;
}

const KeyMetricCard: React.FC<KeyMetricCardProps> = ({
  color,
  title,
  value,
  measurement,
  subtitle,
  backgroundColor
}) => {
  const valueRef = useRef<HTMLSpanElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);

  // Determine background color based on the provided color
  const getBackgroundColor = () => {
    // If color is a hex code, create a lighter version
    if (color.startsWith('#')) {
      // Convert to a lighter shade by adding opacity
      return `${color}15`; // Adds 15% opacity for light background
    }
    // Otherwise return a default light version
    return '#F5F5F5';
  };

  // Calculate scale to fit text within container
  useEffect(() => {
    const calculateScale = () => {
      // Small delay to ensure DOM is ready
      setTimeout(() => {
        if (!valueRef.current || !containerRef.current) return;

        const container = containerRef.current;
        const valueElement = valueRef.current;

        // Reset scale to get true measurements
        valueElement.style.transform = 'none';

        // Force a reflow to ensure the browser recalculates
        void valueElement.offsetWidth;

        // Get measurements
        const containerWidth = container.offsetWidth;
        const valueWidth = valueElement.scrollWidth;

        // Calculate available width (container width - padding - buffer for measurement unit)
        const paddingTotal = 32; // 16px padding on each side
        const bufferForUnit = measurement ? 40 : 20; // Extra space if there's a measurement unit
        const availableWidth = containerWidth - paddingTotal - bufferForUnit;

        // Calculate scale if text is too wide
        if (valueWidth > availableWidth && availableWidth > 0) {
          const newScale = Math.max(0.5, Math.min(1, availableWidth / valueWidth));
          valueElement.style.transform = `scale(${newScale})`;
          setScale(newScale);
        } else {
          valueElement.style.transform = 'scale(1)';
          setScale(1);
        }
      }, 0);
    };

    // Calculate on mount and value change
    calculateScale();

    // Recalculate on window resize with debounce
    let resizeTimeout: NodeJS.Timeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(calculateScale, 100);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(resizeTimeout);
    };
  }, [value, measurement]);

  return (
    <div
      className="metric-card"
      style={{ backgroundColor: backgroundColor || getBackgroundColor() }}
      ref={containerRef}
    >
      <div className="metric-card-label">{title}</div>
      <div className="metric-card-value-container">
        <span
          className={`metric-main-value`}
          ref={valueRef}
          style={{
            color,
            transformOrigin: 'left center',
            display: 'inline-block',
            whiteSpace: 'nowrap',
            transition: 'transform 0.2s ease-in-out'
          }}
        >
          {value}
        </span>
        {measurement && (
          <span className="metric-unit" style={{
            color,
            transform: `scale(${scale})`,
            transformOrigin: 'left center',
            display: 'inline-block',
            marginLeft: scale < 1 ? `${2 * scale}px` : '2px',
            transition: 'transform 0.2s ease-in-out, margin-left 0.2s ease-in-out'
          }}>
            {measurement}
          </span>
        )}
      </div>
      {subtitle && (
        <div className="metric-subtitle">{subtitle}</div>
      )}
    </div>
  );
};

export default KeyMetricCard;

// Export backward compatibility alias
export { KeyMetricCard as KPICard };